import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ServerContextService } from '../../../../feature-vault/src/lib/servers/server-context.service';
import type {
  AssignUserTenantPayload,
  UpdateUserTenantRolePayload,
  UserTenantAssignment,
} from '../../../../domain/src/lib/admin-users/admin-user.model';
import { AdminUsersService } from './admin-users.service';
import { mapAdminUserToTenantAssignments } from './admin-user-tenants.util';
import { isUserTenantsEndpointMissing } from './admin-user-tenants.util';
import { VAULT_ADMIN_USER_TENANTS_API_ENABLED } from './admin-user-tenants.config';
import {
  resolveTenantId,
  tenantHeaders,
  withTenantParam,
} from '../shared/tenant-context.util';

@Injectable({ providedIn: 'root' })
export class AdminUserTenantsService {
  private readonly http = inject(HttpClient);
  private readonly adminUsers = inject(AdminUsersService);
  private readonly serverContext = inject(ServerContextService);
  private readonly userTenantsApiEnabled = inject(VAULT_ADMIN_USER_TENANTS_API_ENABLED);
  private readonly basePath = '/api/admin/user-tenants';

  /**
   * Asignaciones Vault del usuario.
   * Con API montado: prioriza `GET /api/admin/user-tenants/user/{id}` (incluye `user_tenant_id` real).
   * Fallback: `tenants[]` de `GET /api/admin/users/{id}` o listado.
   */
  listByUser(userId: string): Observable<UserTenantAssignment[]> {
    if (!this.userTenantsApiEnabled) {
      return this.loadTenantsFromUsersApi(userId);
    }
    return this.loadTenantsFromUserTenantsApi(userId).pipe(
      switchMap((fromUserTenants) =>
        fromUserTenants.length ? of(fromUserTenants) : this.loadTenantsFromUsersApi(userId),
      ),
      catchError((err: unknown) => {
        if (isUserTenantsEndpointMissing(err)) {
          return this.loadTenantsFromUsersApi(userId);
        }
        return throwError(() => err);
      }),
    );
  }

  private loadTenantsFromUsersApi(userId: string): Observable<UserTenantAssignment[]> {
    return this.adminUsers.get(userId).pipe(
      map((user) => mapAdminUserToTenantAssignments(user)),
      catchError(() =>
        this.adminUsers.list().pipe(
          map((users) => {
            const user = users.find((u) => u.id === userId);
            return user ? mapAdminUserToTenantAssignments(user) : [];
          }),
          catchError(() => of([])),
        ),
      ),
    );
  }

  private loadTenantsFromUserTenantsApi(userId: string): Observable<UserTenantAssignment[]> {
    const tenantId = resolveTenantId(this.serverContext);
    const params = withTenantParam(tenantId);
    return this.http
      .get<unknown>(`${this.basePath}/user/${userId}`, {
        headers: tenantHeaders(tenantId),
        ...(params ? { params } : {}),
      })
      .pipe(
        map((raw) => this.normalizeListFromUserTenantsApi(raw)),
        catchError((err: unknown) => {
          if (isUserTenantsEndpointMissing(err)) {
            return of([]);
          }
          return throwError(() => err);
        }),
      );
  }

  assign(payload: AssignUserTenantPayload): Observable<UserTenantAssignment> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http
      .post<unknown>(this.basePath, payload, {
        headers: tenantHeaders(tenantId),
        ...(p ? { params: p } : {}),
      })
      .pipe(
        map((raw) => this.normalizeOne(raw)),
        catchError((err: unknown) => throwError(() => err)),
      );
  }

  updateRole(
    userTenantId: string,
    payload: UpdateUserTenantRolePayload,
  ): Observable<UserTenantAssignment> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http
      .put<unknown>(`${this.basePath}/${userTenantId}`, payload, {
        headers: tenantHeaders(tenantId),
        ...(p ? { params: p } : {}),
      })
      .pipe(
        map((raw) => this.normalizeOne(raw)),
        catchError((err: unknown) => throwError(() => err)),
      );
  }

  remove(userTenantId: string): Observable<void> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http
      .delete<void>(`${this.basePath}/${userTenantId}`, {
        headers: tenantHeaders(tenantId),
        ...(p ? { params: p } : {}),
      })
      .pipe(
        catchError((err: unknown) => throwError(() => err)),
      );
  }

  private normalizeListFromUserTenantsApi(raw: unknown): UserTenantAssignment[] {
    if (!Array.isArray(raw)) return [];
    const out: UserTenantAssignment[] = [];
    for (const entry of raw) {
      const o = entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : {};
      const tenant_id = String(o['tenant_id'] ?? o['tenantId'] ?? '').trim();
      if (!tenant_id) continue;
      out.push({
        id: String(o['id'] ?? ''),
        user_id: String(o['user_id'] ?? o['userId'] ?? ''),
        tenant_id,
        role: String(o['role'] ?? ''),
        tenant_name: o['tenant_name'] != null ? String(o['tenant_name']) : undefined,
        tenant_slug: o['tenant_slug'] != null ? String(o['tenant_slug']) : undefined,
        has_server: o['has_server'] === true || o['hasServer'] === true,
      });
    }
    return out;
  }

  private normalizeOne(raw: unknown): UserTenantAssignment {
    const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    return {
      id: String(o['id'] ?? ''),
      user_id: String(o['user_id'] ?? ''),
      tenant_id: String(o['tenant_id'] ?? ''),
      role: String(o['role'] ?? ''),
      tenant_name: o['tenant_name'] != null ? String(o['tenant_name']) : undefined,
      tenant_slug: o['tenant_slug'] != null ? String(o['tenant_slug']) : undefined,
      has_server: o['has_server'] === true,
    };
  }
}
